import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { ContactMessageBody } from '@voice2me/shared';
import { api } from '../api/client.js';
import { SiteLayout } from './SiteChrome.js';

export function ContactPage() {
  useEffect(() => {
    document.title = 'Contact | Voice to Value';
  }, []);

  const [form, setForm] = useState<ContactMessageBody>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const send = useMutation({
    mutationFn: (input: ContactMessageBody) => api.post<{ ok: true }>('/contact', input),
  });

  const update =
    (field: keyof ContactMessageBody) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: event.target.value }));

  return (
    <SiteLayout>
      <section className="v2v-contact">
        <div className="shell">
          <div className="divider" />
          <p className="eyebrow">Get in touch</p>
          <h1>Contact me</h1>
          <p className="contact-lead">
            Have a question or want to see Voice to Value in action? Send a message and I&rsquo;ll
            get back to you.
          </p>

          {send.isSuccess ? (
            <div className="contact-done" role="status">
              <h2>Thanks, your message is on its way.</h2>
              <p>I&rsquo;ll reply to {form.email} as soon as I can.</p>
            </div>
          ) : (
            <form
              className="v2v-form"
              onSubmit={(event) => {
                event.preventDefault();
                send.mutate(form);
              }}
            >
              <div className="field">
                <label htmlFor="contact-name">Your name</label>
                <input
                  id="contact-name"
                  type="text"
                  required
                  autoComplete="name"
                  value={form.name}
                  onChange={update('name')}
                />
              </div>

              <div className="field">
                <label htmlFor="contact-email">Your email</label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={update('email')}
                />
              </div>

              <div className="field">
                <label htmlFor="contact-subject">Subject</label>
                <input
                  id="contact-subject"
                  type="text"
                  required
                  value={form.subject}
                  onChange={update('subject')}
                />
              </div>

              <div className="field">
                <label htmlFor="contact-message">Your message</label>
                <textarea
                  id="contact-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={update('message')}
                />
              </div>

              {send.isError && (
                <p className="form-error" role="alert">
                  Sorry, something went wrong sending your message. Please try again.
                </p>
              )}

              <button className="btn btn-primary" type="submit" disabled={send.isPending}>
                {send.isPending ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
